import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import api, { apiError } from "../lib/api";
import { useLang } from "../i18n";
import { Plus, ChevronLeft, ChevronRight, CalendarDays, MapPin, X, Search, Link2 } from "lucide-react";

const MONTHS = {
  fr: ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"],
  en: ["January","February","March","April","May","June","July","August","September","October","November","December"],
};
const DAYS = { fr: ["L","M","M","J","V","S","D"], en: ["M","T","W","T","F","S","S"] };

function toKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function parseLocal(s) {
  if (!s) return null;
  const [y, m, d] = s.slice(0, 10).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

export default function EventsCalendar() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(new Date());
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(() => localStorage.getItem("mv_event_sort") || "date_desc");
  const setSortMode = (s) => { setSort(s); localStorage.setItem("mv_event_sort", s); };
  const [form, setForm] = useState({ name: "", type: "concert", start_date: "", end_date: "", location: "", url: "", comment: "" });

  const load = async () => { const { data } = await api.get("/events"); setEvents(data); };
  useEffect(() => { load(); }, []);

  const filteredEvents = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return events;
    return events.filter((ev) =>
      ev.name.toLowerCase().includes(q) ||
      (ev.start_date || "").includes(q) ||
      (ev.end_date || "").includes(q)
    );
  }, [events, search]);

  const sortedEvents = useMemo(() => {
    const arr = [...filteredEvents];
    arr.sort((a, b) => {
      switch (sort) {
        case "name_asc": return a.name.localeCompare(b.name);
        case "name_desc": return b.name.localeCompare(a.name);
        case "date_asc": return (a.start_date || "").localeCompare(b.start_date || "");
        default: return (b.start_date || "").localeCompare(a.start_date || "");
      }
    });
    return arr;
  }, [filteredEvents, sort]);

  const eventDays = useMemo(() => {
    const map = {};
    filteredEvents.forEach((ev) => {
      const start = parseLocal(ev.start_date);
      if (!start) return;
      const end = ev.end_date ? parseLocal(ev.end_date) : start;
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const k = toKey(d);
        (map[k] = map[k] || []).push(ev);
      }
    });
    return map;
  }, [filteredEvents]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDay = new Date(year, month, 1);
  const startOffset = (firstDay.getDay() + 6) % 7; // Monday first
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const save = async () => {
    if (!form.name.trim() || !form.start_date) return;
    setError("");
    try {
      await api.post("/events", { ...form, end_date: form.end_date || null });
      setShowForm(false);
      setForm({ name: "", type: "concert", start_date: "", end_date: "", location: "", url: "", comment: "" });
      load();
    } catch (err) { setError(apiError(err.response?.data?.detail) || err.message); }
  };

  const inputCls = "w-full bg-black border border-zinc-800 rounded-sm px-3 py-2.5 text-white focus:border-primary focus:outline-none";
  const labelCls = "label-caps text-xs text-zinc-400";

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <h1 className="heading text-4xl font-bold text-white">{t("calendar")}</h1>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
            <div className="relative w-full sm:w-72">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input data-testid="event-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("search_event")}
                className="w-full bg-black border border-zinc-800 rounded-sm pl-10 pr-3 py-2.5 text-white focus:border-primary focus:outline-none" />
            </div>
            <button data-testid="add-event-btn" onClick={() => setShowForm(true)}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground label-caps text-sm rounded-sm font-semibold hover:bg-yellow-400 whitespace-nowrap w-full sm:w-auto">
              <Plus size={16} /> {t("add_event")}
            </button>
          </div>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-5 mb-8">
          <div className="flex items-center justify-between mb-4">
            <button data-testid="cal-prev" onClick={() => setCursor(new Date(year, month - 1, 1))} className="p-2 text-zinc-400 hover:text-white"><ChevronLeft size={20} /></button>
            <h2 className="heading text-xl text-white">{MONTHS[lang][month]} {year}</h2>
            <button data-testid="cal-next" onClick={() => setCursor(new Date(year, month + 1, 1))} className="p-2 text-zinc-400 hover:text-white"><ChevronRight size={20} /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 mb-2">
            {DAYS[lang].map((d, i) => <div key={i} className="text-center label-caps text-xs text-zinc-500 py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) => {
              if (!d) return <div key={`empty-${i}`} className="min-h-[68px]" />;
              const evs = eventDays[toKey(d)] || [];
              const has = evs.length > 0;
              return (
                <div key={toKey(d)} data-testid={has ? `cal-day-event-${toKey(d)}` : `cal-day-${toKey(d)}`}
                  className={`min-h-[68px] rounded-sm p-1 flex flex-col gap-0.5 border ${has ? "bg-primary/10 border-primary/40" : "border-transparent"}`}>
                  <span className={`text-xs px-1 ${has ? "text-primary font-semibold" : "text-zinc-500"}`}>{d.getDate()}</span>
                  {evs.slice(0, 2).map((ev) => (
                    <button key={ev.id} data-testid={`cal-event-${ev.id}`} onClick={() => navigate(`/events/${ev.id}`)} title={ev.name}
                      className="w-full text-left truncate text-[10px] leading-tight px-1 py-0.5 rounded-sm bg-primary/25 text-primary hover:bg-primary/40 transition-colors">
                      {ev.name}
                    </button>
                  ))}
                  {evs.length > 2 && <span className="text-[10px] text-zinc-400 px-1">+{evs.length - 2}</span>}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <h2 className="label-caps text-sm text-zinc-400">{t("events")}</h2>
          <select data-testid="event-sort-select" value={sort} onChange={(e) => setSortMode(e.target.value)}
            className="bg-black border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-200 focus:border-primary focus:outline-none">
            <option value="date_desc">{t("sort_date_desc")}</option>
            <option value="date_asc">{t("sort_date_asc")}</option>
            <option value="name_asc">{t("sort_az")}</option>
            <option value="name_desc">{t("sort_za")}</option>
          </select>
        </div>
        {sortedEvents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <CalendarDays size={48} className="text-zinc-700 mb-4" />
            <p className="text-zinc-400">{t("no_events")}</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {sortedEvents.map((ev) => (
              <button key={ev.id} data-testid="event-list-item" onClick={() => navigate(`/events/${ev.id}`)}
                className="text-left flex items-center justify-between bg-zinc-900 border border-zinc-800 rounded-sm px-4 py-4 hover:border-zinc-600 transition-colors">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="label-caps text-xs px-2 py-0.5 bg-zinc-800 text-primary rounded-sm">{t(ev.type)}</span>
                    <h3 className="heading text-lg text-white">{ev.name}</h3>
                    {ev.url && (
                      <span role="button" tabIndex={0} data-testid={`event-link-badge-${ev.id}`} title={ev.url}
                        onClick={(e) => { e.stopPropagation(); window.open(ev.url, "_blank", "noopener"); }}
                        className="inline-flex items-center text-primary hover:text-yellow-400 cursor-pointer">
                        <Link2 size={16} />
                      </span>
                    )}
                  </div>
                  <p className="text-zinc-500 text-sm mt-1 flex items-center gap-3">
                    <span className="flex items-center gap-1"><CalendarDays size={14} /> {ev.start_date}{ev.end_date ? ` → ${ev.end_date}` : ""}</span>
                    {ev.location && <span className="flex items-center gap-1"><MapPin size={14} /> {ev.location}</span>}
                  </p>
                </div>
                <span className="text-zinc-400 text-sm whitespace-nowrap">
                  <span className="text-primary font-semibold">{ev.concert_count}</span> {t("concerts_seen_of")} / {ev.total_concert_count ?? ev.concert_count} {t("concerts_planned")}
                </span>
              </button>
            ))}
          </div>
        )}
      </main>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setShowForm(false)}>
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="heading text-2xl text-white">{t("add_event")}</h3>
              <button onClick={() => setShowForm(false)} className="text-zinc-400 hover:text-white"><X size={20} /></button>
            </div>
            {error && <div className="mb-3 text-sm text-red-400">{error}</div>}
            <div className="space-y-3">
              <div><label className={labelCls}>{t("event_name")}</label><input data-testid="event-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("search_placeholder_event")} className={inputCls} /></div>
              <div><label className={labelCls}>{t("type")}</label>
                <select data-testid="event-type-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls}>
                  <option value="concert">{t("concert")}</option>
                  <option value="festival">{t("festival")}</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>{t("start_date")}</label><input data-testid="event-start-input" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className={inputCls} /></div>
                <div><label className={labelCls}>{t("end_date")}</label><input data-testid="event-end-input" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className={inputCls} /></div>
              </div>
              <div><label className={labelCls}>{t("location")}</label><input data-testid="event-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>{t("event_url")}</label><input data-testid="event-url-input" type="url" placeholder="https://..." value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className={inputCls} /></div>
              <button data-testid="save-event-btn" onClick={save} className="w-full bg-primary text-primary-foreground label-caps py-3 rounded-sm font-semibold hover:bg-yellow-400">{t("save")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
